import { useState, useRef, useEffect } from 'react';
import { CaretDown, Check, Buildings, Stack } from '@phosphor-icons/react';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, isAdmin, isManager } from '@/auth/AuthContext';
import { useDepartment } from './DepartmentContext';

const ALL = { id: 'all', name: '全部門', color: '#71717A', code: 'ALL' };

// Japanese `name` is the canonical value. The legacy `name_en` column is kept in
// Mongo for rollback but is NO LONGER used for display: English rendering now
// comes from the central LLM translation layer (see i18n/translationEngine.js),
// so one authoritative translation path exists for all dynamic content.
function deptName(t, lang, d) {
  if (d.id === 'all') return t('mfg.dept.all');
  return d.name;
}

/** Header dropdown to switch the active 製造 department. */
export function DepartmentSelector({ hideAll = false }) {
  const { t, lang } = useT();
  const { user } = useAuth();
  const { departments, activeId, setActive, locked } = useDepartment();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const canAll = !hideAll && !locked && (isAdmin(user) || isManager(user));

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const options = canAll ? [ALL, ...departments] : departments;
  const current = activeId === 'all' ? ALL : departments.find((d) => d.id === activeId);

  // A8 · 部門が固定された現場ユーザーには切替メニューを出さない（表示だけ）
  if (locked) {
    return (
      <div className="flex items-center gap-2 h-10 pl-3 pr-3 rounded-lg border border-zinc-200 bg-zinc-50" data-testid="mfg-dept-locked">
        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: current?.color || '#71717A' }} />
        <span className="text-sm font-semibold text-zinc-700 max-w-[180px] truncate">
          {current ? deptName(t, lang, current) : t('mfg.dept.unassigned')}
        </span>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref} data-testid="mfg-dept-selector">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="mfg-dept-selector-btn"
        className="flex items-center gap-2 h-10 pl-3 pr-2.5 rounded-lg border border-zinc-200 bg-white hover:bg-zinc-50 transition-colors shadow-sm">
        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: current?.color || '#71717A' }} />
        <span className="text-sm font-semibold text-zinc-800 max-w-[160px] truncate">
          {current ? deptName(t, lang, current) : t('mfg.dept.select')}
        </span>
        <CaretDown size={14} className={`text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-1.5 w-64 rounded-xl border border-zinc-200 bg-white shadow-lg z-50 overflow-hidden" data-testid="mfg-dept-selector-menu">
          <p className="px-3 pt-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">{t('mfg.dept.switch')}</p>
          {options.map((d) => (
            <button key={d.id} data-testid={`mfg-dept-option-${d.code || d.id}`}
              onClick={() => { setActive(d.id); setOpen(false); }}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left hover:bg-zinc-50 transition-colors">
              {d.id === 'all'
                ? <Stack size={16} weight="duotone" className="text-zinc-500 shrink-0" />
                : <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: d.color }} />}
              <span className="flex-1 text-sm text-zinc-700 truncate">{deptName(t, lang, d)}</span>
              {activeId === d.id && <Check size={15} weight="bold" className="text-orange-500" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** First-visit full screen to pick a department. */
export function DepartmentPicker({ hideAll = false }) {
  const { t, lang } = useT();
  const { user } = useAuth();
  const { departments, setActive } = useDepartment();
  const canAll = !hideAll && (isAdmin(user) || isManager(user));
  const options = canAll ? [...departments, ALL] : departments;

  return (
    <div className="max-w-3xl mx-auto py-10 page-enter" data-testid="mfg-dept-picker">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-teal-700 text-white mb-4 shadow-sm">
          <Buildings size={26} weight="duotone" />
        </div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('mfg.dept.pickTitle')}</h1>
        <p className="text-sm text-zinc-500 mt-1.5">{t('mfg.dept.pickSubtitle')}</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {options.map((d) => (
          <button key={d.id} data-testid={`mfg-dept-pick-${d.code || d.id}`}
            onClick={() => setActive(d.id)}
            className="group flex items-center gap-4 p-5 rounded-lg border-2 border-zinc-200 bg-white hover:border-orange-300 hover:shadow-md transition-all text-left"
            style={d.id !== 'all' ? { borderLeftColor: d.color, borderLeftWidth: 6 } : undefined}>
            {d.id === 'all'
              ? <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-zinc-100 text-zinc-500 shrink-0"><Stack size={24} weight="duotone" /></span>
              : <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl text-white shrink-0" style={{ background: d.color }}><Buildings size={24} weight="duotone" /></span>}
            <div className="min-w-0">
              <p className="font-bold text-zinc-900 truncate">{deptName(t, lang, d)}</p>
              {/* iter_152: in JA mode we show just the code (e.g. "SOL") — never
                  the English name — so users don't see "Soldering Department"
                  under "ソルダリング製造部". In EN mode the title is already
                  the EN name, so the subtitle can safely fall back to the code. */}
              <p className="text-xs text-zinc-400 truncate">
                {d.id === 'all' ? t('mfg.dept.allHint') : (d.code || '')}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
